// Eingangsrechnung – Belegart 'supplier_invoice' (ER-). Der Einkauf erfasst sie aus Wareneingängen oder Bestellungen,
// gleicht je Zeile ab (bestellt · geliefert · berechnet, Menge und Preis) und gibt sie frei. Mit der Freigabe
// („Festschreiben“) entsteht über den Kern der offene Posten (Verbindlichkeit) – ab da gehört sie den Finanzen:
// Zahlung, Skonto, Export. Was das Modul Finanzen liest, steht unten (invoicePayables, invoiceReleased).
//
//   In Prüfung (Entwurf) → Freigegeben (festgeschrieben, offener Posten) → Storniert
import { PERMISSION_COSTS_READ, defineContract, defineEvent } from '@shared/contract'
import { defineDocumentContracts, zDocumentView } from '@shared/document'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { EXTRA_LINE_KINDS, isChargeKind } from '../lib/labels'
import { DIFFERENCE_TYPES, MATCH_DECISIONS } from '../lib/match'
import { GOODS_RECEIPT_TYPE, INVOICE_TYPE, ORDER_TYPE, zDay, zReason, zSupplierRef } from './common'

const PERMISSIONS = {
  read: 'purchasing.invoice.read',
  write: 'purchasing.invoice.check',
  post: 'purchasing.invoice.check',
  reverse: 'purchasing.invoice.check',
} as const

export const INVOICE_SOURCE_TYPES = [GOODS_RECEIPT_TYPE, ORDER_TYPE] as const

export const invoice = defineDocumentContracts({
  module: 'purchasing',
  entity: 'invoice',
  documentType: INVOICE_TYPE,
  title: 'Eingangsrechnung',
  titlePlural: 'Eingangsrechnungen',
  permissions: PERMISSIONS,
  derivesFrom: INVOICE_SOURCE_TYPES,
  keywords: ['Eingangsrechnung', 'Lieferantenrechnung', 'Rechnungsprüfung', 'Rechnung vom Lieferanten', 'Kreditor', 'ER'],
})

export const invoiceGet = invoice.get
export const invoiceList = invoice.list
export const invoiceSaveDraft = invoice.saveDraft
/** Freigeben: nur ohne offene Abweichung – erzeugt den offenen Posten */
export const invoicePost = invoice.post
export const invoiceReverse = invoice.reverse
export const invoiceTransition = invoice.transition
export const invoicePrint = invoice.print
export const invoiceDiscard = invoice.discard
export const invoiceDerive = invoice.derive

// ---- Bausteine des Erfassens (auch für die Lieferantengutschrift) --------------------------------------------------

const zPrice = z.int({ error: 'Bitte einen Preis eingeben.' }).min(0, { error: 'Der Preis darf nicht negativ sein.' }).max(100_000_000, { error: 'Der Preis ist zu hoch.' })
/** Preiseinheit in Tausendstel wie im Belegvertrag: 1000 = je Gebinde, 100_000 = je 100 */
const zPricePerQty = z.int({ error: 'Bitte eine Preiseinheit eingeben.' }).min(1, { error: 'Die Preiseinheit muss größer als 0 sein.' }).max(100_000_000, { error: 'Die Preiseinheit ist zu groß.' })
const zDiscountBp = z.int().min(0, { error: 'Der Rabatt liegt zwischen 0 und 100 %.' }).max(10_000, { error: 'Der Rabatt liegt zwischen 0 und 100 %.' })
const zLineQty = z.int({ error: 'Bitte eine Menge eingeben.' }).min(1, { error: 'Die Menge muss größer als 0 sein.' }).max(1_000_000_000, { error: 'Die Menge ist zu groß.' })
const zText = (max: number) => z.string().trim().max(max, { error: `Der Text ist zu lang (höchstens ${max} Zeichen).` })

/**
 * Zusatzzeile einer erfassten Rechnung (AP8a): Zuschlag (Fracht, Maut, Energiezuschlag), Abzug, Pfand ohne Warenzeile
 * und Leergut-Gutschrift des Lieferanten. Die Menge ist ein Betrag – das Vorzeichen setzt die Art (Abzug und Leergut
 * mindern). Pfand und Leergut bewertet der Kern mit dem Betrag der Pfandart; ein abweichender Preis der Rechnung steht
 * am Abgleich.
 */
export const zCaptureExtraLine = z
  .strictObject({
    kind: z.enum(EXTRA_LINE_KINDS, { error: 'Bitte wählen Sie die Art der Zusatzzeile: Zuschlag, Abzug, Pfand oder Leergut.' }),
    /** wie auf der Rechnung („Frachtzuschlag“); bei Pfand und Leergut ohne Angabe der Name der Pfandart */
    description: zText(500).nullish(),
    /** Betrag der Menge in Tausendstel (1000 = 1; bei Pfand und Leergut Stück der Pfandart) */
    qty: zLineQty.default(1000),
    /** Cent netto je Preiseinheit, nie negativ – Pflicht bei Zuschlag und Abzug */
    unitPriceCents: zPrice.optional(),
    pricePerQty: zPricePerQty.optional(),
    /** Pflicht bei Zuschlag und Abzug; bei Pfand ohne Angabe der Schlüssel der Pfandart */
    taxCodeId: zId.optional(),
    /** Pflicht bei Pfand und Leergut */
    depositTypeId: zId.optional(),
  })
  .refine((line) => !isChargeKind(line.kind) || line.taxCodeId !== undefined, { error: 'Bitte wählen Sie den Steuerschlüssel für Zuschlag bzw. Abzug.', path: ['taxCodeId'] })
  .refine((line) => !isChargeKind(line.kind) || line.unitPriceCents !== undefined, { error: 'Bitte geben Sie den Betrag des Zuschlags bzw. Abzugs an.', path: ['unitPriceCents'] })
  .refine((line) => !isChargeKind(line.kind) || Boolean(line.description?.trim()), { error: 'Bitte geben Sie eine Bezeichnung an (z. B. „Frachtzuschlag“).', path: ['description'] })
  .refine((line) => isChargeKind(line.kind) || line.depositTypeId !== undefined, { error: 'Bitte wählen Sie die Pfandart.', path: ['depositTypeId'] })
  .refine((line) => !isChargeKind(line.kind) || line.depositTypeId === undefined, { error: 'Zuschläge und Abzüge haben keine Pfandart.', path: ['depositTypeId'] })
  .refine((line) => line.pricePerQty === undefined || line.unitPriceCents !== undefined, { error: 'Zur Preiseinheit gehört ein Preis.', path: ['unitPriceCents'] })
export type CaptureExtraLine = z.output<typeof zCaptureExtraLine>

/** Artikelzeile ohne Bezug auf Bestellung oder Wareneingang (AP8b) – der Abgleich zeigt sie als „Ohne Bestellbezug“. */
export const zCaptureItemLine = z
  .strictObject({
    itemId: zId,
    /** Gebinde des Artikels (item_unit); ohne Angabe das Einkaufsgebinde */
    unitId: zId.optional(),
    /** Menge im Gebinde (Tausendstel) */
    qty: zLineQty,
    /** Cent netto je Preiseinheit */
    unitPriceCents: zPrice,
    pricePerQty: zPricePerQty.optional(),
    discountBp: zDiscountBp.optional(),
    /** überschreibt den Steuerschlüssel des Artikels */
    taxCodeId: zId.optional(),
    /** Hinweis zur Zeile (z. B. die Bezeichnung des Lieferanten) */
    note: zText(500).nullish(),
  })
export type CaptureItemLine = z.output<typeof zCaptureItemLine>

export const invoiceCapture = defineContract({
  name: 'purchasing.invoice.capture',
  title: 'Eingangsrechnung erfassen',
  description:
    'Erfasst die Rechnung eines Lieferanten mit seiner Rechnungsnummer und seinem Datum – aus Wareneingängen oder Bestellungen, mit Belegrabatt, Zuschlägen und Abzügen, oder ohne Wareneingang (Dienstleistung, Kosten).',
  llm: {
    description:
      'Erfasst eine Eingangsrechnung in einem Schritt: Rechnungsnummer und -datum des Lieferanten und die Belege, auf ' +
      'die sie sich bezieht – gebuchte Wareneingänge (üblich) oder Bestellungen desselben Lieferanten. Je Zeile wird ' +
      'die noch nicht berechnete Menge zum bestellten Preis übernommen; mit `lines` lassen sich Menge (im Gebinde der ' +
      'Zeile, Tausendstel), Preis (Cent netto) und Preiseinheit (`pricePerQty`, 100000 = je 100) so setzen, wie sie auf ' +
      'der Rechnung stehen (`listedOnly`: nur diese Zeilen). `headerDiscountBp` = Rabatt auf den Warenwert, ' +
      '`extraLines` = Zuschläge (fee), Abzüge (discount), Gebindewerte ohne Warenzeile (deposit) und Gutschriften für ' +
      'zurückgegebene Gebinde (deposit_return), `extraItems` = Artikel ohne Bestellbezug. Ohne `sourceIds` (Dienstleistung, Kosten) braucht ' +
      'es `supplierId` und `locationId`. Die Rechnung ist danach „In Prüfung“ – gebucht ist nichts. Abgleich: ' +
      'purchasing.invoice.match; Freigabe: purchasing.invoice.post. Dieselbe Rechnungsnummer desselben Lieferanten ' +
      'lässt sich nicht zweimal erfassen.',
    keywords: ['Rechnung erfassen', 'Eingangsrechnung', 'Lieferantenrechnung buchen', 'Rechnung zum Wareneingang', 'Frachtzuschlag', 'Rechnung ohne Wareneingang'],
  },
  input: z
    .strictObject({
      /** Rechnungsnummer des Lieferanten */
      supplierInvoiceNo: z
        .string({ error: 'Bitte die Rechnungsnummer des Lieferanten eingeben.' })
        .trim()
        .min(1, { error: 'Bitte die Rechnungsnummer des Lieferanten eingeben.' })
        .max(120),
      /** Rechnungsdatum des Lieferanten – Beginn des Zahlungsziels */
      invoiceDate: zDay,
      /** Wareneingänge oder Bestellungen desselben Lieferanten; leer = Rechnung ohne Wareneingang (dann supplierId und locationId) */
      sourceIds: z.array(zId).max(50).default([]),
      /** Lieferant – Pflicht ohne `sourceIds`; mit `sourceIds` muss er der Lieferant der Belege sein */
      supplierId: zId.optional(),
      /** Standort der Rechnung – Pflicht ohne `sourceIds`; mit `sourceIds` der Standort des ersten Belegs */
      locationId: zId.optional(),
      lines: z
        .array(
          z
            .strictObject({
              /** Zeile des Wareneingangs bzw. der Bestellung */
              sourceLineId: zId,
              /** berechnete Menge im Gebinde der Zeile (Tausendstel) */
              qty: zLineQty.optional(),
              /** berechneter Preis in Cent netto je Preiseinheit */
              unitPriceCents: zPrice.optional(),
              /** Preiseinheit des berechneten Preises (Tausendstel des Gebindes); ohne Angabe die der Zeile */
              pricePerQty: zPricePerQty.optional(),
              /** Zeilenrabatt laut Rechnung in Basispunkten; ohne Angabe der der Zeile */
              discountBp: zDiscountBp.optional(),
            })
            .refine((line) => line.pricePerQty === undefined || line.unitPriceCents !== undefined, { error: 'Zur Preiseinheit gehört ein Preis.', path: ['unitPriceCents'] }),
        )
        .max(500)
        .optional(),
      /** true: nur die in `lines` genannten Zeilen der Belege übernehmen – sonst alle noch nicht berechneten */
      listedOnly: z.boolean().default(false),
      /** Rabatt auf den Warenwert des ganzen Belegs (ohne Pfand und Zuschläge) in Basispunkten */
      headerDiscountBp: zDiscountBp.optional(),
      /** Artikel ohne Bestellbezug */
      extraItems: z.array(zCaptureItemLine).max(100, { error: 'Höchstens 100 Artikelzeilen ohne Bezug.' }).optional(),
      /** Zuschläge, Abzüge, Pfand ohne Warenzeile, Leergut-Gutschriften */
      extraLines: z.array(zCaptureExtraLine).max(100, { error: 'Höchstens 100 Zusatzzeilen.' }).optional(),
      notes: z.string().trim().max(2000).nullish(),
    })
    .refine((input) => input.sourceIds.length > 0 || input.supplierId !== undefined, {
      error: 'Ohne Wareneingang oder Bestellung bitte den Lieferanten wählen.',
      path: ['supplierId'],
    })
    .refine((input) => input.sourceIds.length > 0 || input.locationId !== undefined, {
      error: 'Ohne Wareneingang oder Bestellung bitte den Standort der Rechnung wählen.',
      path: ['locationId'],
    })
    .refine((input) => !input.listedOnly || (input.lines?.length ?? 0) > 0, {
      error: 'Bitte nennen Sie die Zeilen, die übernommen werden sollen.',
      path: ['lines'],
    }),
  output: zDocumentView,
  permission: PERMISSIONS.write,
  risk: 'write',
  scope: 'record',
  limits: {
    maxItems: 500,
    itemCount: (input) => (input.lines?.length ?? input.sourceIds.length) + (input.extraItems?.length ?? 0) + (input.extraLines?.length ?? 0),
  },
  fieldPermissions: { 'lines.*.costCents': PERMISSION_COSTS_READ },
})

export const zMatchDifference = z.object({
  type: z.enum(DIFFERENCE_TYPES),
  label: z.string(),
  blocking: z.boolean(),
  /** berechnet − geliefert in Tausendstel der Basiseinheit */
  qtyDelta: z.int(),
  /** Cent netto, positiv = zu unseren Lasten */
  amountCents: z.int(),
  message: z.string(),
})

export const zMatchDecision = z.object({
  decision: z.enum(MATCH_DECISIONS),
  reason: z.string(),
  byName: z.string(),
  at: z.string(),
  /** angeforderte Gutschrift in Cent netto (bei `credit`) */
  creditCents: z.int(),
})

export const zMatchLine = z.object({
  lineId: zId,
  pos: z.int(),
  itemId: zId.nullable(),
  sku: z.string().nullable(),
  description: z.string(),
  /** Kurzform und Basismenge des Gebindes der Rechnungszeile */
  unit: z.string().nullable(),
  unitBaseQty: z.int(),
  /** ok | info | difference | decided */
  status: z.enum(['ok', 'info', 'difference', 'decided']),
  /** Bezug der Zeile */
  reference: z.enum(['receipt', 'order']).nullable(),
  order: z.object({ id: zId, number: z.string().nullable(), lineId: zId }).nullable(),
  receipt: z.object({ id: zId, number: z.string().nullable(), lineId: zId }).nullable(),
  // die drei Mengen im Gebinde der Rechnungszeile (Tausendstel); null = dazu gibt es nichts
  orderedQty: z.int().nullable(),
  receivedQty: z.int().nullable(),
  invoicedQty: z.int(),
  /** bereits durch andere freigegebene Rechnungen berechnet */
  invoicedBeforeQty: z.int(),
  damagedQty: z.int(),
  // Preise je Gebinde der Rechnungszeile in Cent netto (nach Zeilenrabatt, auf 1 Gebinde gerechnet)
  orderedPriceCents: z.int().nullable(),
  invoicedPriceCents: z.int(),
  /** Zeilenbetrag der Rechnung und derselben Menge zum bestellten Preis */
  invoicedNetCents: z.int(),
  expectedNetCents: z.int().nullable(),
  priceDeltaCents: z.int(),
  differences: z.array(zMatchDifference),
  decision: zMatchDecision.nullable(),
  /**
   * Vorschlag, den berechneten Preis als neuen Einkaufspreis zu hinterlegen (nur bei Preisabweichung und nur als
   * Vorschlag – übernommen wird er erst mit purchasing.invoice.adoptPrice)
   */
  priceProposal: z
    .object({
      orderUnit: z.string().nullable(),
      /** Cent netto je Bestelleinheit */
      currentCents: z.int().nullable(),
      proposedCents: z.int(),
      validFrom: z.string(),
      /** der hinterlegte Preis stimmt bereits mit der Rechnung überein */
      adopted: z.boolean(),
    })
    .nullable(),
})
export type MatchLine = z.output<typeof zMatchLine>

/** Zusatzzeile im Abgleich: Zuschlag, Abzug, Pfand ohne Warenzeile, Leergut-Gutschrift (AP8a) */
export const zMatchExtraLine = z.object({
  lineId: zId,
  pos: z.int(),
  kind: z.enum(EXTRA_LINE_KINDS),
  /** Zuschlag · Abzug · Pfand · Leergut-Gutschrift */
  kindLabel: z.string(),
  description: z.string(),
  /** Betrag der Menge (Tausendstel; bei Pfand und Leergut Stück der Pfandart) */
  qty: z.int(),
  unit: z.string().nullable(),
  unitPriceCents: z.int(),
  pricePerQty: z.int(),
  /** Betrag netto mit Vorzeichen (Abzug und Leergut negativ) */
  netCents: z.int(),
  taxCodeId: zId.nullable(),
  taxRateBp: z.int().nullable(),
  depositTypeId: zId.nullable(),
  /** übernommen aus Bestellung bzw. Wareneingang – sonst null (Zeile nur auf der Rechnung) */
  reference: z.enum(['receipt', 'order']).nullable(),
  source: z.object({ id: zId, number: z.string().nullable(), lineId: zId }).nullable(),
  /** Betrag derselben Menge im Vorgänger (nur mit Bezug) */
  expectedNetCents: z.int().nullable(),
  /** Pfand und Leergut: Betrag laut Rechnung, wenn er vom Betrag der Pfandart abweicht */
  statedNetCents: z.int().nullable(),
  status: z.enum(['ok', 'info', 'difference', 'decided']),
  differences: z.array(zMatchDifference),
  decision: zMatchDecision.nullable(),
  /** ein Satz zur Zeile */
  message: z.string(),
})
export type MatchExtraLine = z.output<typeof zMatchExtraLine>

export const zInvoiceMatch = z.object({
  id: zId,
  number: z.string().nullable(),
  status: z.string(),
  statusLabel: z.string(),
  supplier: zSupplierRef.nullable(),
  supplierInvoiceNo: z.string().nullable(),
  invoiceDate: z.string(),
  dueDate: z.string().nullable(),
  tolerances: z.object({ qtyToleranceBp: z.int(), priceToleranceBp: z.int(), priceToleranceCents: z.int() }),
  lines: z.array(zMatchLine),
  /**
   * Zuschläge, Abzüge, Pfand ohne Warenzeile und Leergut-Gutschriften (in der Reihenfolge der Rechnung). Der Server
   * liefert sie immer; optional nur, damit bestehende Aufrufer und Attrappen ohne das Feld gültig bleiben (AP8a).
   */
  extraLines: z.array(zMatchExtraLine).optional(),
  deposit: z.object({
    /** Pfand auf der Rechnung (brutto) – ohne Leergut-Gutschriften */
    invoicedCents: z.int(),
    /** Leergut-Gutschriften auf der Rechnung (brutto, als positiver Betrag) – immer geliefert (AP8a) */
    returnedCents: z.int().optional(),
    /** Pfand, das die Wareneingänge dazu auf das Leergutkonto des Lieferanten gebucht haben (brutto); null = kein Bezug */
    receivedCents: z.int().nullable(),
    deltaCents: z.int(),
    /** Stand des Leergutkontos beim Lieferanten in Cent (positiv = wir haben Pfand bezahlt, das Leergut steht bei uns) */
    accountCents: z.int().nullable(),
    blocking: z.boolean(),
    message: z.string().nullable(),
    decision: zMatchDecision.nullable(),
  }),
  summary: z.object({
    /** Artikelzeilen */
    lineCount: z.int(),
    /** Zusatzzeilen – immer geliefert (AP8a) */
    extraCount: z.int().optional(),
    okCount: z.int(),
    /** Abweichungen, die noch entschieden werden müssen (Zeilen und Pfand) */
    openCount: z.int(),
    decidedCount: z.int(),
    /** Summe der sperrenden Abweichungen in Cent netto */
    differenceCents: z.int(),
    /** Summe der angeforderten Gutschriften in Cent netto */
    creditRequestedCents: z.int(),
    netCents: z.int(),
    grossCents: z.int(),
    /** die Rechnung lässt sich freigeben (keine offene Abweichung, noch nicht freigegeben) */
    releasable: z.boolean(),
    /** ein Satz zum Stand: „2 Abweichungen offen – bitte annehmen oder Gutschrift anfordern.“ */
    message: z.string(),
  }),
})
export type InvoiceMatch = z.output<typeof zInvoiceMatch>

export const invoiceMatch = defineContract({
  name: 'purchasing.invoice.match',
  title: 'Rechnung mit Bestellung und Wareneingang abgleichen',
  description: 'Abgleich je Zeile: bestellt, geliefert, berechnet – Menge und Preis.',
  llm: {
    description:
      'Liefert den Abgleich einer Eingangsrechnung: je Zeile bestellte, gelieferte und berechnete Menge (im Gebinde ' +
      'der Rechnungszeile, Tausendstel), bestellter und berechneter Preis, die Abweichungen über der Toleranz mit ' +
      'fertigem Satz und ihre Entscheidung (angenommen mit Grund oder Gutschrift angefordert); dazu weitere Beträge der ' +
      'Rechnung gegen das, was die Wareneingänge auf Konten des Lieferanten gebucht haben. `summary` sagt, ' +
      'ob sich die Rechnung freigeben lässt. Verändert nichts.',
    keywords: ['Rechnungsprüfung', 'Abgleich', 'Preisabweichung', 'Mengenabweichung', 'stimmt die Rechnung', 'Drei-Wege-Abgleich'],
  },
  input: z.strictObject({ id: zId }),
  output: zInvoiceMatch,
  permission: PERMISSIONS.read,
  risk: 'read',
  scope: 'record',
})

export const invoiceDecide = defineContract({
  name: 'purchasing.invoice.decide',
  title: 'Abweichung der Rechnung entscheiden',
  description: 'Nimmt die Abweichung einer Rechnungszeile mit Grund an oder fordert dafür eine Gutschrift an.',
  llm: {
    description:
      'Entscheidet die Abweichung EINER Zeile einer Rechnung in Prüfung (ohne `lineId`: die Abweichung außerhalb der Zeilen): ' +
      '`accept` nimmt sie mit Grund hin (die Rechnung wird in voller Höhe zahlbar), `credit` fordert beim Lieferanten ' +
      'eine Gutschrift über den Betrag der Abweichung an – der Einkauf erhält dafür eine Aufgabe –, `reset` nimmt ' +
      'die Entscheidung zurück. Ändert sich die Zeile danach, verfällt die Entscheidung. Erst wenn jede Abweichung ' +
      'entschieden ist, lässt sich die Rechnung freigeben.',
    keywords: ['Abweichung annehmen', 'Gutschrift anfordern', 'Preisabweichung akzeptieren', 'Reklamation'],
  },
  input: z.strictObject({
    id: zId,
    /** Zeile der Rechnung; ohne Angabe die Abweichung beim Pfand */
    lineId: zId.nullish(),
    decision: z.enum([...MATCH_DECISIONS, 'reset']),
    reason: zReason.optional(),
  }),
  output: zInvoiceMatch,
  permission: PERMISSIONS.write,
  risk: 'write',
  scope: 'record',
})

export const invoiceAdoptPrice = defineContract({
  name: 'purchasing.invoice.adoptPrice',
  title: 'Preis der Rechnung als Einkaufspreis hinterlegen',
  description: 'Hinterlegt den berechneten Preis einer Rechnungszeile als neuen, datierten Einkaufspreis in den Stammdaten.',
  llm: {
    description:
      'Übernimmt den Preis einer Rechnungszeile mit Preisabweichung als neuen Einkaufspreis des Artikels bei diesem ' +
      'Lieferanten – über die Stammdaten (masterdata.itemSupplier.set): gültig ab dem Rechnungsdatum oder `validFrom`, ' +
      'der bisherige Preis endet am Vortag und bleibt in der Historie. Nur auf ausdrücklichen Wunsch der einkaufenden ' +
      'Person; nie von selbst. Verlangt zusätzlich das Recht, Einkaufspreise zu pflegen.',
    keywords: ['Einkaufspreis übernehmen', 'neuen EK hinterlegen', 'Preiserhöhung übernehmen'],
  },
  input: z.strictObject({ id: zId, lineId: zId, validFrom: zDay.optional() }),
  output: z.object({ itemId: zId, supplierId: zId, priceCents: z.int(), validFrom: z.string(), message: z.string() }),
  permission: PERMISSIONS.write,
  risk: 'financial',
  scope: 'record',
})

export const zInvoiceSource = z.object({
  id: zId,
  /** goods_receipt */
  type: z.string(),
  number: z.string().nullable(),
  documentDate: z.string(),
  /** Lieferschein-Nummer des Lieferanten */
  externalRef: z.string().nullable(),
  supplier: zSupplierRef,
  locationId: zId,
  locationCode: z.string(),
  /** Bestellungen, zu denen der Wareneingang gehört */
  orderNumbers: z.array(z.string()),
  /** Zeilen mit noch nicht berechneter Menge */
  openLineCount: z.int(),
  /** „238 Ka Hell · 80 Ka Edelstoff“ */
  summary: z.string(),
  /** noch nicht berechneter Warenwert in Cent netto (zum Preis des Wareneingangs) */
  openNetCents: z.int(),
  /** dazu ist bereits eine Rechnung in Prüfung */
  inCheck: z.object({ id: zId, supplierInvoiceNo: z.string().nullable() }).nullable(),
})

export const invoiceSources = defineContract({
  name: 'purchasing.invoice.sources',
  title: 'Noch nicht berechnete Wareneingänge anzeigen',
  description: 'Gebuchte Wareneingänge, zu denen noch keine Rechnung freigegeben ist – die Auswahl beim Erfassen einer Eingangsrechnung.',
  llm: {
    description:
      'Listet gebuchte Wareneingänge mit noch nicht berechneten Mengen an den Standorten, die der Nutzer sehen darf – ' +
      'je Wareneingang Lieferant, Lieferschein-Nummer, Bestellung, offene Zeilen und ob dazu schon eine Rechnung in ' +
      'Prüfung ist. Verwenden vor purchasing.invoice.capture („Zu welcher Lieferung gehört die Rechnung?“) und für ' +
      '„Welche Lieferungen sind noch nicht berechnet?“.',
    keywords: ['unberechnete Lieferungen', 'Rechnung fehlt', 'Wareneingang ohne Rechnung', 'Rechnung zuordnen'],
  },
  input: z.strictObject({ supplierId: zId.optional(), limit: z.int().min(1).max(200).default(100) }),
  output: z.object({ rows: z.array(zInvoiceSource) }),
  permission: PERMISSIONS.read,
  risk: 'read',
  scope: 'any',
})

// ---- Was das Modul Finanzen liest ----------------------------------------------------------------------------------

export const zPayable = z.object({
  /** Beleg der Eingangsrechnung */
  documentId: zId,
  /** unsere Belegnummer ER-… */
  number: z.string(),
  supplier: zSupplierRef,
  /** Rechnungsnummer und -datum des Lieferanten */
  supplierInvoiceNo: z.string().nullable(),
  invoiceDate: z.string(),
  locationId: zId,
  releasedAt: z.date(),
  releasedByName: z.string().nullable(),
  /** offener Posten des Kerns (open_item.id) – Zahlungen ordnet payments.recordPayment zu */
  openItemId: zId,
  netCents: z.int(),
  taxCents: z.int(),
  grossCents: z.int(),
  /** Pfandsaldo brutto, in grossCents enthalten */
  depositCents: z.int(),
  openCents: z.int(),
  dueDate: z.string().nullable(),
  discountUntil: z.string().nullable(),
  discountBp: z.int(),
  /** beim Lieferanten angeforderte Gutschriften in Cent netto – noch nicht eingegangen, mindern den Posten nicht */
  creditRequestedCents: z.int(),
  /** mit Grund angenommene Abweichungen in Cent netto */
  acceptedDifferenceCents: z.int(),
})

export const invoicePayables = defineContract({
  name: 'purchasing.invoice.payables',
  title: 'Freigegebene Eingangsrechnungen anzeigen',
  description: 'Geprüfte und freigegebene Eingangsrechnungen mit ihrem offenen Posten – die Übergabe an die Finanzen.',
  llm: {
    description:
      'Listet freigegebene Eingangsrechnungen mit offenem Posten: Lieferant, seine Rechnungsnummer, Beträge, Fälligkeit, ' +
      'Skontofrist, offener Betrag und angeforderte Gutschriften. Verwenden für „Welche Lieferantenrechnungen sind zu ' +
      'zahlen?“. Rechnungen in Prüfung stehen in purchasing.invoice.list.',
    keywords: ['zahlbar', 'Verbindlichkeiten', 'offene Lieferantenrechnungen', 'fällig', 'Kreditoren', 'Zahlungslauf'],
  },
  input: z.strictObject({
    supplierId: zId.optional(),
    /** nur Posten mit offenem Betrag (Standard) */
    openOnly: z.boolean().default(true),
    dueUntil: zDay.optional(),
    limit: z.int().min(1).max(500).default(200),
  }),
  output: z.object({ rows: z.array(zPayable), openCents: z.int() }),
  permission: PERMISSIONS.read,
  risk: 'read',
  scope: 'any',
})

/** Eine Eingangsrechnung ist geprüft und freigegeben – der offene Posten existiert (Abnehmer: Finanzen). */
export const invoiceReleased = defineEvent({
  name: 'purchasing.invoice.released',
  title: 'Eingangsrechnung freigegeben',
  payload: z.object({
    documentId: zId,
    number: z.string(),
    supplierId: zId,
    locationId: zId,
    openItemId: zId.nullable(),
    grossCents: z.int(),
    dueDate: z.string().nullable(),
    creditRequestedCents: z.int(),
  }),
})
